import {application} from 'configs/app';
import EventEmitter from 'eventemitter3';
import api, {ApiError} from 'services/Api';
import store from 'store';
import SnackbarUtil from 'utils/snackbar';
import {handleMessage} from 'utils/websocket-handler';

const _singleton = Symbol();
const _singletonEnforcer = Symbol();

export const WEBSOCKET_STATUS = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  CLOSED: 'CLOSED',
  DUPLICATED: 'DUPLICATED',
};

export default class WebSocketApi extends EventEmitter {
  _websocket = null;
  _pending = new Map();
  _uid = 0;

  constructor(enforcer) {
    super();
    if (enforcer !== _singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }
  }

  /**
   * Get WebSocketApi instance
   * @returns {WebSocketApi}
   */
  static get instance() {
    if (!this[_singleton]) {
      this[_singleton] = new WebSocketApi(_singletonEnforcer);
    }
    return this[_singleton];
  }

  _onOpen = async () => {
    try {
      this._heartbeatInteral = setInterval(() => {
        this.invoke(
          {_: 'HeartbeatRequest'},
          {
            api: {
              withAuth: false,
            },
          },
        );
      }, 10000);

      this._websocket._connectionPromise.resolve();
      await this.authenticate();
      this._websocket._authPromise.resolve();
      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CONNECTED);
    } catch (e) {
      this.disconnect();
      throw e;
    }
  };

  _onClose = (event) => {
    if (event.code === 4001) {
      this._strictConnection = false;
    }
    this._onTerminate();
    if (event.code === 4001) {
      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.DUPLICATED);
    }
  };

  _onTerminate = () => {
    store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CLOSED);
    if (this._heartbeatInteral !== undefined) {
      clearInterval(this._heartbeatInteral);
      this._heartbeatInteral = undefined;
    }
    if (this._websocket) {
      const rejectError = new WebSocketApiError(
        undefined,
        new Error(),
        '84لطفا دقایقی دیگر مجدد تلاش کنید یا اینترنت خود را بررسی نمایید ',
      );
      this._websocket._connectionPromise.reject(rejectError);
      this._websocket._authPromise.reject(rejectError);
      this._websocket = null;
      if (this._strictConnection) {
        setTimeout(async () => {
          if (localStorage.getItem('refreshToken') !== null) {
            this.ensureConnection();
          }
        }, 1000);
      }

      this._pending.forEach((request) => {
        request.reject(rejectError);
      });
    }
  };

  _onMessage = async (message) => {
    const response = JSON.parse(message.data);
    if (response.header.status === 401) {
      api.resetCachedToken();
      const auth = this.authenticate();

      const request = this._pending.get(response.id);

      if (request) {
        if (request.body?._ === 'AuthenticateRequest') {
          return request.resolve(auth);
        } else {
          await auth;
          return request.resolve(this.invoke(request.body));
        }
      }
    }

    let request;
    if (response.id) {
      request = this._pending.get(response.id);

      if (response.header.status >= 400) {
        let message;

        if (response.body && response.body.message) {
          message = response.body.message;
        } else {
          message = 'خطایی رخ داده است';
        }

        request.reject(
          new WebSocketApiError(response.body, new Error(), message),
        );
      } else {
        request.resolve(response);
      }
    }

    handleMessage(response, request);

    if (response?.body?._ === 'PushSocketResponse') {
      this.emit('PushSocket', response.body);
    }
  };

  ensureConnection = (withAuth = true) => {
    this._strictConnection = true;
    if (!this._websocket) {
      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CONNECTING);
      this._websocket = new WebSocket(application.webSocketEndpoint);
      this._websocket.onmessage = this._onMessage;
      this._websocket.onopen = this._onOpen;
      this._websocket.onclose = this._onClose;
      this._websocket.onerror = this._onTerminate;

      this._websocket._connectionPromise = {};
      this._websocket._connectionPromise.promise = new Promise(
        (resolve, reject) => {
          this._websocket._connectionPromise.resolve = resolve;
          this._websocket._connectionPromise.reject = reject;
        },
      );

      this._websocket._authPromise = {};
      this._websocket._authPromise.promise = new Promise((resolve, reject) => {
        this._websocket._authPromise.resolve = resolve;
        this._websocket._authPromise.reject = reject;
      });
    }
    if (withAuth) {
      return this._websocket._authPromise.promise;
    }
    return this._websocket._connectionPromise.promise;
  };

  disconnect = () => {
    this._strictConnection = false;
    if (this._websocket) {
      this._websocket.close();
    }
  };

  authenticate = async () => {
    const {access_token: accessToken} = await api.getCachedToken();
    return this.invoke(
      {
        _: 'AuthenticateRequest',
        authorization: 'Bearer ' + accessToken,
      },
      {
        api: {
          withAuth: false,
        },
      },
    );
  };

  invoke = async (body, options) => {
    const apiOptions = {
      emitNotification: false,
      withAuth: true,
      ...((options && options.api) || {}),
    };

    if (options) {
      delete options.api;
    }

    const id = ++this._uid;

    try {
      await this.ensureConnection(apiOptions.withAuth);
      await this._websocket.send(
        JSON.stringify({
          id,
          body,
        }),
      );

      return new Promise((resolve, reject) => {
        this._pending.set(id, {
          body,
          resolve: (value) => {
            this._pending.delete(id);
            resolve(value);
          },
          reject: (e) => {
            this._pending.delete(id);
            let rejectError = e;

            if (!(e instanceof ApiError)) {
              rejectError = new WebSocketApiError(
                undefined,
                e,
                'خطایی رخ داده است',
              );
            }
            reject(rejectError);

            if (apiOptions.emitNotification) {
              SnackbarUtil.error(rejectError.message);
            }
          },
        });
      });
    } catch (e) {
      if (!(e instanceof ApiError)) {
        throw new WebSocketApiError(undefined, e, 'خطایی رخ داده است');
      }

      throw e;
    }
  };
}

class WebSocketApiError extends ApiError {
  constructor(...params) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, WebSocketApiError);
    }
    this.name = 'WebSocketApiError';
  }
}
